/* eslint-disable no-console */
/* global globalThis */
/**
 * Web Crypto polyfill for INSECURE (plain-HTTP) contexts.
 *
 * keycloak-js >= 26 requires two secure-context-only Web Crypto APIs that are
 * absent on plain HTTP (e.g. http://testserver):
 *   - crypto.randomUUID()      -> login `state` & `nonce` (createLoginUrl, called unconditionally)
 *   - crypto.subtle.digest()   -> PKCE S256 code challenge (SHA-256 of the code verifier)
 *
 * crypto.getRandomValues() IS available on plain HTTP, so we build both from it
 * (UUIDv4 from random bytes; SHA-256 in pure JS). This lets the full auth flow —
 * including PKCE S256 — work on HTTP exactly as it does on HTTPS, so the app no
 * longer depends on a secure context to log in.
 *
 * Only patches what is missing: on HTTPS / localhost the native implementations
 * are present and left untouched. Must be imported BEFORE keycloak-js is used
 * (it is the first import in src/index.js).
 */

// Pure-JS SHA-256. Returns the 32-byte digest as a Uint8Array.
const sha256Bytes = (bytes) => {
    const K = [
        0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
        0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
        0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
        0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
        0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
        0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
        0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
        0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
    ];
    const rotr = (n, x) => (x >>> n) | (x << (32 - n));

    const l = bytes.length;
    const padded = new Uint8Array(((((l + 8) >> 6) + 1) * 64));
    padded.set(bytes);
    padded[l] = 0x80;
    const bitLen = l * 8;
    const dv = new DataView(padded.buffer);
    dv.setUint32(padded.length - 4, bitLen >>> 0);
    dv.setUint32(padded.length - 8, Math.floor(bitLen / 0x100000000));

    let H = [0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19];

    for (let i = 0; i < padded.length; i += 64) {
        const w = new Uint32Array(64);
        for (let t = 0; t < 16; t++) w[t] = dv.getUint32(i + t * 4);
        for (let t = 16; t < 64; t++) {
            const s0 = rotr(7, w[t - 15]) ^ rotr(18, w[t - 15]) ^ (w[t - 15] >>> 3);
            const s1 = rotr(17, w[t - 2]) ^ rotr(19, w[t - 2]) ^ (w[t - 2] >>> 10);
            w[t] = (w[t - 16] + s0 + w[t - 7] + s1) | 0;
        }
        let [a, b, c, d, e, f, g, h] = H;
        for (let t = 0; t < 64; t++) {
            const S1 = rotr(6, e) ^ rotr(11, e) ^ rotr(25, e);
            const ch = (e & f) ^ (~e & g);
            const t1 = (h + S1 + ch + K[t] + w[t]) | 0;
            const S0 = rotr(2, a) ^ rotr(13, a) ^ rotr(22, a);
            const maj = (a & b) ^ (a & c) ^ (b & c);
            const t2 = (S0 + maj) | 0;
            h = g; g = f; f = e; e = (d + t1) | 0; d = c; c = b; b = a; a = (t1 + t2) | 0;
        }
        H = [(H[0] + a) | 0, (H[1] + b) | 0, (H[2] + c) | 0, (H[3] + d) | 0, (H[4] + e) | 0, (H[5] + f) | 0, (H[6] + g) | 0, (H[7] + h) | 0];
    }

    const out = new Uint8Array(32);
    const odv = new DataView(out.buffer);
    for (let i = 0; i < 8; i++) odv.setUint32(i * 4, H[i] >>> 0);
    return out;
};

const installInsecureCryptoPolyfill = () => {
    const g = typeof window !== 'undefined' ? window : globalThis;
    const cryptoObj = g && g.crypto;

    // getRandomValues is our only primitive; without it we can do nothing.
    if (!cryptoObj || typeof cryptoObj.getRandomValues !== 'function') {
        return;
    }

    // --- crypto.randomUUID (RFC 4122 v4) ---
    if (typeof cryptoObj.randomUUID !== 'function') {
        const hex = new Array(256);
        for (let i = 0; i < 256; i++) hex[i] = (i + 0x100).toString(16).slice(1);
        const randomUUID = () => {
            const b = cryptoObj.getRandomValues(new Uint8Array(16));
            b[6] = (b[6] & 0x0f) | 0x40; // version 4
            b[8] = (b[8] & 0x3f) | 0x80; // variant 10xx
            return (
                hex[b[0]] + hex[b[1]] + hex[b[2]] + hex[b[3]] + '-' +
                hex[b[4]] + hex[b[5]] + '-' +
                hex[b[6]] + hex[b[7]] + '-' +
                hex[b[8]] + hex[b[9]] + '-' +
                hex[b[10]] + hex[b[11]] + hex[b[12]] + hex[b[13]] + hex[b[14]] + hex[b[15]]
            );
        };
        try {
            Object.defineProperty(cryptoObj, 'randomUUID', { value: randomUUID, configurable: true, writable: true });
            console.warn('(crypto-polyfill) crypto.randomUUID polyfilled for insecure (plain-HTTP) context.');
        } catch (e) {
            console.error('(crypto-polyfill) failed to define crypto.randomUUID', e);
        }
    }

    // --- crypto.subtle.digest('SHA-256', data) ---
    const hasNativeDigest = cryptoObj.subtle && typeof cryptoObj.subtle.digest === 'function';
    if (!hasNativeDigest) {
        const digest = async (algorithm, data) => {
            const name = (typeof algorithm === 'string' ? algorithm : algorithm && algorithm.name) || '';
            if (String(name).toUpperCase() !== 'SHA-256') {
                throw new Error(`(crypto-polyfill) unsupported digest algorithm "${name}" (only SHA-256 is polyfilled).`);
            }
            let input;
            if (data instanceof Uint8Array) input = data;
            else if (data instanceof ArrayBuffer) input = new Uint8Array(data);
            else if (data && data.buffer) input = new Uint8Array(data.buffer, data.byteOffset || 0, data.byteLength);
            else throw new TypeError('(crypto-polyfill) digest expects a BufferSource.');
            return sha256Bytes(input).buffer;
        };
        try {
            Object.defineProperty(cryptoObj, 'subtle', { value: { digest }, configurable: true, writable: true });
            console.warn('(crypto-polyfill) crypto.subtle.digest(SHA-256) polyfilled for insecure (plain-HTTP) context.');
        } catch (e) {
            console.error('(crypto-polyfill) failed to define crypto.subtle', e);
        }
    }
};

installInsecureCryptoPolyfill();

export default installInsecureCryptoPolyfill;
